import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Theme
import { ThemeProvider } from "./contexts/ThemeContext";

// Components
import InstallPrompt from "./components/InstallPrompt";
import FirstLoginDialog from "./components/FirstLoginDialog";
import NotificationManager from "./components/NotificationManager";
import ViewAsSelector from "./components/ViewAsSelector";
import PoloChatbot from "./components/PoloChatbot";

// Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import MemberCard from "./pages/MemberCard";
import Games from "./pages/Games";
import Events from "./pages/Events";
import Calendar from "./pages/Calendar";
import Discounts from "./pages/Discounts";
import AdminPanel from "./pages/AdminPanel";
import AdminMigrations from "./pages/AdminMigrations";
import AcademyTeamsManagement from "./pages/AcademyTeamsManagement";
import Teams from "./pages/Teams";
import TeamDetails from "./pages/TeamDetails";
import MerchantScanner from "./pages/MerchantScanner";
import Merchants from "./pages/Merchants";
import MerchantApplication from "./pages/MerchantApplication";
import CoachApplication from "./pages/CoachApplication";
import CommitteeAvailability from "./pages/CommitteeAvailability";
import Notifications from "./pages/Notifications";
import NotificationPreferences from "./pages/NotificationPreferences";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";
import CalendarSettings from "./pages/CalendarSettings";
import CalendarCallback from "./pages/CalendarCallback";
import CalendarManager from "./pages/CalendarManager";
import MeetingFinder from "./pages/MeetingFinder";
import OutlookCallback from "./pages/OutlookCallback";
import GoogleCallback from "./pages/GoogleCallback";
import UserRightsManagement from "./pages/UserRightsManagement";
import MatchReports from "./pages/MatchReports";
import MerchantApply from "./pages/MerchantApply";
import AcademyPlaceholder from "./pages/AcademyPlaceholder";
import AcademyDashboard from "./pages/AcademyDashboard";
import AcademyMyKids from "./pages/AcademyMyKids";
import AcademyTeams from "./pages/AcademyTeams";
import AcademyTeamDetails from "./pages/AcademyTeamDetails";
import AcademyTeamSettings from "./pages/AcademyTeamSettings";
import AcademyTermManagement from "./pages/AcademyTermManagement";
import AcademyRateTableManagement from "./pages/AcademyRateTableManagement";
import AcademyRateTableConfig from "./pages/AcademyRateTableConfig";
import ParentRegistrationWizard from "./pages/ParentRegistrationWizard";

// Wrapper to force remount of registration wizard on each navigation
// Also passes user prop to enable existing member recognition
function ParentRegistrationWizardWrapper({ user }) {
  const location = useLocation();
  console.log("ParentRegistrationWizardWrapper - user prop:", user ? user.email : "null");
  // Use location.key to force remount when navigating to this route
  // This ensures form always resets to step 1
  return <ParentRegistrationWizard key={location.key} user={user} />;
}

// Wrapper for landing page to handle preview mode
function LandingPageWrapper({ user, setUser }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isPreview = searchParams.get('preview') === 'true';
  
  // If preview mode or not logged in, show landing page
  if (isPreview || !user) {
    return <LandingPage setUser={setUser} />;
  }
  
  // Otherwise redirect to dashboard
  return <Navigate to="/dashboard" />;
}

import RegistrationSuccess from "./pages/RegistrationSuccess";
import PayNowPayment from "./pages/PayNowPayment";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AcademyEnrollment from "./pages/AcademyEnrollment";
import AcademySessions from "./pages/AcademySessions";
import AttendanceHub from "./pages/AttendanceHub";
import NotFound from "./pages/NotFound";
import CoachPay from "./pages/CoachPay";
import AcademyMatchReport from "./pages/AcademyMatchReport";
import AcademyMatchReportView from "./pages/AcademyMatchReportView";
import AcademyPlayerDashboard from "./pages/AcademyPlayerDashboard";
import AcademyFeedback from "./pages/AcademyFeedback";
import AcademyPlayerFeedback from "./pages/AcademyPlayerFeedback";
import AcademyAttendance from "./pages/AcademyAttendance";
// Replaced by AttendanceHub (one place for sessions, grid and coach check-ins).
// import CoachAttendanceHub from "./pages/CoachAttendanceHub";
import CoachSquadBuilder from "./pages/CoachSquadBuilder";
import AcademyBrowseTeams from "./pages/AcademyBrowseTeams";
import GameCalendar from "./pages/GameCalendar";
import AdminPaymentVerification from "./pages/AdminPaymentVerification";
import AcademyLocationManagement from "./pages/AcademyLocationManagement";
import AcademyUnassignedPlayers from "./pages/AcademyUnassignedPlayers";
import AcademyPlayerOverview from "./pages/AcademyPlayerOverview";
import ConsentTextManagement from "./pages/ConsentTextManagement";
import RegistrationNoticeManagement from "./pages/RegistrationNoticeManagement";
import AdminApprovals from "./pages/AdminApprovals";
import AdminWalkIns from "./pages/AdminWalkIns";
import AdminTrials from "./pages/AdminTrials";
import AdminTrialSurveys from "./pages/AdminTrialSurveys";
import AcademyHoldingGroup from "./pages/AcademyHoldingGroup";
import AcademyAdhocSignup from "./pages/AcademyAdhocSignup";
import TrialSurvey from "./pages/TrialSurvey";
import RegistrationNoticePage from "./pages/RegistrationNoticePage";
import OutstandingPaymentPage from "./pages/OutstandingPaymentPage";
import SessionUpgrade from "./pages/SessionUpgrade";
import ParentDashboard from "./pages/ParentDashboard";
import SecondParentFamilyView from "./pages/SecondParentFamilyView";
import CoachDashboard from "./pages/CoachDashboard";
import CoachKitHandover from "./pages/CoachKitHandover";
import CoachKitNumbers from "./pages/CoachKitNumbers";
import LeagueFixtures from "./pages/LeagueFixtures";
import CoachManagement from "./pages/CoachManagement";
import SessionManagement from "./pages/SessionManagement";
import AdhocPaymentPage from "./pages/AdhocPaymentPage";
import DataManagement from "./pages/DataManagement";
import MyInvoices from "./pages/MyInvoices";
import CommitteePortal from "./pages/CommitteePortal";
import MemberManagement from "./pages/MemberManagement";
import AdminDiscountCodes from "./pages/AdminDiscountCodes";
import AdminMerchantDiscounts from "./pages/AdminMerchantDiscounts";
import AdminMerchants from "./pages/AdminMerchants";
import AdminDisclaimerSettings from "./pages/AdminDisclaimerSettings";
import AdminBrandingSettings from "./pages/AdminBrandingSettings";
import AdminMediaLibrary from "./pages/AdminMediaLibrary";
import AdminPDPASettings from "./pages/AdminPDPASettings";
import PlayerFeedback from "./pages/coach/PlayerFeedback";
import CoachBroadcast from "./pages/coach/CoachBroadcast";
import AdminForceLogout from "./pages/AdminForceLogout";
import AdminWhatsAppSettings from "./pages/AdminWhatsAppSettings";
import AdminFeeMatrix from "./pages/AdminFeeMatrix";
import AdminHandbook from "./pages/AdminHandbook";
import HandbookReader from "./pages/HandbookReader";
import AdminDataSync from "./pages/AdminDataSync";
import AdminManualInvoice from "./pages/AdminManualInvoice";
import AdminRemindersDue from "./pages/AdminRemindersDue";
import AdminFeeReconciliation from "./pages/AdminFeeReconciliation";
import AdminPaymentsHub from "./pages/AdminPaymentsHub";
import AcademyInbox from "./pages/AcademyInbox";
import AdminEvents from "./pages/AdminEvents";
import AdminMasterQRs from "./pages/AdminMasterQRs";
import AdminFeeMismatches from "./pages/AdminFeeMismatches";
import AdminTerm3Reconciliation from "./pages/AdminTerm3Reconciliation";
import AdminDuplicateSessions from "./pages/AdminDuplicateSessions";
import AdminDuplicateRegistrations from "./pages/AdminDuplicateRegistrations";
import PublicEventPage from "./pages/PublicEventPage";
import AdminTermPayments from "./pages/AdminTermPayments";
import AdminPendingCleanup from "./pages/AdminPendingCleanup";
import AdminKitManagement from "./pages/AdminKitManagement";
import AdminDuplicatePlayers from "./pages/AdminDuplicatePlayers";
import ParentKitOrdersPage from "./pages/ParentKitOrdersPage";
import AdminCredits from "./pages/AdminCredits";
import AdminRainOut from "./pages/AdminRainOut";
import PDPAConsentModal from "./components/PDPAConsentModal";
import RegistrationPipeline from "./pages/RegistrationPipeline";
import { AlertTriangle, Shield } from "lucide-react";

// PDPA Limited Access Banner Component
function PDPALimitedAccessBanner({ onViewTerms }) {
  return (
    <div className="fixed top-0 left-0 right-0 z-[90] bg-gradient-to-r from-amber-500 to-amber-600 text-black px-4 py-2 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">
            Limited Access Mode - Please accept our Terms & Consent to unlock full features
          </span>
        </div>
        <button
          onClick={onViewTerms}
          className="px-3 py-1 bg-black/20 hover:bg-black/30 rounded text-sm font-medium transition-colors flex items-center gap-1"
        >
          <Shield className="w-4 h-4" />
          View Terms
        </button>
      </div>
    </div>
  );
}
import DuplicateAccounts from "./pages/DuplicateAccounts";
import AcademyRoster from "./pages/AcademyRoster";
import AdminRegisterForParent from "./pages/AdminRegisterForParent";
import AdminBillingAdjustments from "./pages/AdminBillingAdjustments";
import PlayerDashboard from "./pages/PlayerDashboard";
import PlayerPasswordReset from "./pages/PlayerPasswordReset";
import PendingApproval from "./pages/PendingApproval";
import HelpCenter from "./pages/HelpCenter";
import HelpAdmin from "./pages/HelpAdmin";
import PublicScanner from "./pages/PublicScanner";
import StaffPortal from "./pages/StaffPortal";
import MeetingMinutes from "./pages/MeetingMinutes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Routes that don't require PDPA acceptance (always accessible when logged in)
const PDPA_FREE_ROUTES = ['/profile', '/scanner', '/forgot-password', '/reset-password', '/player-password-reset', '/parent-registration', '/registration-success', '/academy-enrollment', '/pending-approval'];

// Session token storage key for Safari/cookie fallback
const SESSION_TOKEN_KEY = 'scc_session_token';

// Helper to get stored session token (fallback for Safari)
const getStoredSessionToken = () => localStorage.getItem(SESSION_TOKEN_KEY);
const setStoredSessionToken = (token) => {
  if (token) {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(SESSION_TOKEN_KEY);
  }
};

// Configure axios to include Authorization header as fallback
axios.interceptors.request.use((config) => {
  // Always include credentials for cookie-based auth
  config.withCredentials = true;
  
  // Also add Authorization header as fallback for Safari
  const token = getStoredSessionToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [viewAsRole, setViewAsRole] = useState(null); // Admin can switch views
  const [pdpaStatus, setPdpaStatus] = useState(null); // PDPA status from API
  const [pdpaChecked, setPdpaChecked] = useState(false); // Whether PDPA has been checked
  const [showPdpaModal, setShowPdpaModal] = useState(false); // Control modal visibility

  useEffect(() => {
    // Check for session_id in URL fragment from OAuth
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1].split("&")[0];
      handleOAuthSession(sessionId);
      // Clean URL
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    // Check existing session
    checkSession();
  }, []);

  // Check PDPA status when user is set
  useEffect(() => {
    if (user) {
      checkPdpaStatus();
    } else {
      setPdpaStatus(null);
      setPdpaChecked(false);
    }
  }, [user]);

  const checkPdpaStatus = async () => {
    try {
      const response = await axios.get(`${API}/pdpa/my-status`, { withCredentials: true });
      setPdpaStatus(response.data);
    } catch (error) {
      console.error("Error checking PDPA status:", error);
      // If API fails, assume no PDPA needed to not block user
      setPdpaStatus({ needs_acknowledgment: false, reason: "api_error" });
    } finally {
      setPdpaChecked(true);
    }
  };

  const handleOAuthSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/auth/session-data`, {
        headers: { "X-Session-ID": sessionId }
      });

      if (response.data.error === "not_authorized") {
        toast.error("Account pending authorization. Please contact admin.");
        setLoading(false);
        setSessionChecked(true);
        return;
      }

      // Store session token for Safari fallback
      if (response.data.session_token) {
        setStoredSessionToken(response.data.session_token);
      }

      setUser(response.data.user);
      toast.success("Logged in successfully!");
    } catch (error) {
      console.error("OAuth error:", error);
      toast.error("Authentication failed");
    } finally {
      setLoading(false);
      setSessionChecked(true);
    }
  };

  const checkSession = async () => {
    try {
      // Axios interceptor automatically adds Authorization header if token exists
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.log("No active session");
      // Clear any stored token if session check fails
      setStoredSessionToken(null);
    } finally {
      setLoading(false);
      setSessionChecked(true);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      
      // Clear all local storage and session storage
      localStorage.clear();
      sessionStorage.clear();
      
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      // Even if API fails, clear local data
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
    }
  };

  if (loading || !sessionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 to-amber-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-gray-700 font-medium">Loading SCC Football...</p>
        </div>
      </div>
    );
  }

  // Create effective user object based on viewAsRole
  const getEffectiveUser = () => {
    if (!user) return null;
    if (!viewAsRole) return user; // Admin view
    
    // Return user with overridden role for UI display
    return {
      ...user,
      role: viewAsRole,
      _actualRole: user.role // Keep actual role for reference
    };
  };

  const effectiveUser = getEffectiveUser();

  // Check if PDPA acknowledgment is needed
  const needsPdpaAcknowledgment = pdpaStatus?.needs_acknowledgment === true;

  // Handle logout for PDPA refusal
  const handlePdpaLogout = () => {
    setUser(null);
    setPdpaStatus(null);
    setPdpaChecked(false);
    setShowPdpaModal(false);
    setStoredSessionToken(null);
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/';
  };

  // Handle successful PDPA acknowledgment
  const handlePdpaAcknowledged = () => {
    setPdpaStatus({ needs_acknowledgment: false, reason: "acknowledged" });
    setShowPdpaModal(false);
  };

  // Check if a route is PDPA-free (accessible without consent)
  const isPdpaFreeRoute = (pathname) => {
    return PDPA_FREE_ROUTES.some(route => pathname.startsWith(route)) || pathname === '/';
  };

  // PDPAGate component - monitors route changes and shows modal when needed
  const PDPAGate = () => {
    const location = useLocation();
    const [showModal, setShowModal] = useState(false);
    
    useEffect(() => {
      // If user is logged in, PDPA needs acknowledgment, and route is restricted.
      // Suppress while first_login is true so the mandatory password-change gate
      // (and its escape links) is never obscured by the consent modal.
      if (user && !user.first_login && pdpaChecked && needsPdpaAcknowledgment && !isPdpaFreeRoute(location.pathname)) {
        setShowModal(true);
      } else {
        setShowModal(false);
      }
    }, [location.pathname, needsPdpaAcknowledgment, pdpaChecked]);
    
    if (!showModal) return null;
    
    return (
      <PDPAConsentModal
        user={effectiveUser}
        onAcknowledged={handlePdpaAcknowledged}
        onLogout={handlePdpaLogout}
      />
    );
  };

  return (
    <ThemeProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <InstallPrompt />
        <FirstLoginDialog user={effectiveUser} onPasswordChanged={() => checkSession()} onLogout={logout} />
        {user && <NotificationManager user={effectiveUser} />}
        {user && <ViewAsSelector user={effectiveUser} viewAsRole={viewAsRole} setViewAsRole={setViewAsRole} />}
        {/* POLO is always accessible */}
        <PoloChatbot user={effectiveUser} />
        <BrowserRouter>
          {/* PDPA Limited Access Banner - Shows when logged in but PDPA not yet accepted */}
          {user && pdpaChecked && needsPdpaAcknowledgment && (
            <PDPALimitedAccessBanner onViewTerms={() => setShowPdpaModal(true)} />
          )}
          {/* PDPA Modal - Can be triggered by banner click or by navigating to restricted route */}
          {showPdpaModal && (
            <PDPAConsentModal
              user={effectiveUser}
              onAcknowledged={handlePdpaAcknowledged}
              onLogout={handlePdpaLogout}
            />
          )}
          {/* PDPAGate monitors route changes and shows modal for restricted routes */}
          <PDPAGate />
          <Routes>
            <Route
              path="/"
              element={<LandingPageWrapper user={user} setUser={setUser} />}
            />
            <Route
              path="/scanner"
              element={<PublicScanner />}
            />
            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />
            <Route
              path="/player-password-reset"
              element={<PlayerPasswordReset />}
            />
            <Route
              path="/player-dashboard"
              element={user?.is_player_account ? <PlayerDashboard user={user} logout={logout} /> : <Navigate to="/" />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />
            <Route
              path="/academy/family"
              element={
                user?.roles?.includes("academy_parent_delegate")
                  ? <SecondParentFamilyView user={effectiveUser} logout={logout} />
                  : <Navigate to="/" />
              }
            />
            <Route
              path="/dashboard"
              element={
                user
                  ? (user.roles?.includes("academy_parent_delegate")
                      ? <Navigate to="/academy/family" />
                      : <Dashboard user={effectiveUser} logout={logout} />)
                  : <Navigate to="/" />
              }
            />
            <Route
              path="/pending-approval"
              element={user ? <PendingApproval user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
            />
            <Route
              path="/profile"
              element={user ? <Profile user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
            />
            <Route
              path="/member-card"
              element={user ? <MemberCard user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
            />
            <Route
              path="/games"
              element={user ? <Games user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
            />
            <Route
              path="/game-calendar"
            element={user ? <GameCalendar user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/events"
            element={user ? <Events user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/calendar"
            element={user ? <Calendar user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/discounts"
            element={user ? <Discounts user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/teams"
            element={user ? <Teams user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/teams/:teamId"
            element={user ? <TeamDetails user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin"
            element={
              user && ["convenor", "captain"].includes(user.role) ? (
                <AdminPanel user={effectiveUser} logout={logout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/merchant-scanner"
            element={
              user && ["convenor", "captain", "merchant"].includes(user.role) ? (
                <MerchantScanner user={effectiveUser} logout={logout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/merchants"
            element={
              user && ["convenor", "captain", "merchant"].includes(user.role) ? (
                <Merchants user={effectiveUser} logout={logout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/merchant-application"
            element={user ? <MerchantApplication user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/coach-application"
            element={user ? <CoachApplication user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/committee-availability"
            element={user ? <CommitteeAvailability user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/notifications"
            element={user ? <Notifications user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/notification-preferences"
            element={user ? <NotificationPreferences user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/notification-settings"
            element={user ? <NotificationSettingsPage user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/calendar-settings"
            element={user ? <CalendarSettings user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/calendar-callback"
            element={<CalendarCallback />}
          />
          <Route
            path="/outlook-calendar"
            element={user ? <CalendarManager user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/meeting-finder"
            element={user ? <MeetingFinder user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/meeting-minutes"
            element={user ? <MeetingMinutes user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/meeting-minutes/:meetingId"
            element={user ? <MeetingMinutes user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/help"
            element={<HelpCenter user={effectiveUser} />}
          />
          <Route
            path="/help/:articleSlug"
            element={<HelpCenter user={effectiveUser} />}
          />
          <Route
            path="/admin/help"
            element={user ? <HelpAdmin user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/migration-tools"
            element={<Navigate to="/admin/migrations" replace />}
          />
          <Route
            path="/admin/academy-teams-management"
            element={user && ["convenor", "captain", "academy_admin"].includes(effectiveUser.role) ? <AcademyTeamsManagement user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/staff"
            element={user && ["staff", "convenor", "captain"].includes(effectiveUser.role) ? <StaffPortal user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/outlook-callback"
            element={<OutlookCallback />}
          />
          <Route
            path="/google-callback"
            element={<GoogleCallback />}
          />
          <Route
            path="/user-rights"
            element={user ? <UserRightsManagement user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/match-reports"
            element={user ? <MatchReports user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/match-reports/:reportId"
            element={user ? <MatchReports user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/apply-merchant"
            element={<MerchantApply />}
          />
          <Route
            path="/academy"
            element={user ? <AcademyDashboard user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/my-kids"
            element={user ? <AcademyMyKids user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/teams"
            element={user ? <AcademyTeams user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/teams/:teamId"
            element={user ? <AcademyTeamDetails user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/teams/:teamId/settings"
            element={user ? <AcademyTeamSettings user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/term-management"
            element={user ? <AcademyTermManagement user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/rate-tables"
            element={user ? <AcademyRateTableManagement user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/rate-tables/:rateTableId/config"
            element={user ? <AcademyRateTableConfig user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/start-registration"
            element={<RegistrationNoticePage />}
          />
          <Route
            path="/academy/adhoc-signup"
            element={<AcademyAdhocSignup />}
          />
          <Route
            path="/academy/register"
            element={<ParentRegistrationWizardWrapper user={effectiveUser} />}
          />
          <Route
            path="/academy/registration-success"
            element={<RegistrationSuccess />}
          />
          <Route
            path="/academy/paynow-payment"
            element={<PayNowPayment />}
          />
          <Route
            path="/academy/enrollment"
            element={user ? <AcademyEnrollment user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/sessions"
            element={user ? <AcademySessions user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/sessions/:sessionId/match-report"
            element={user ? <AcademyMatchReport user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/sessions/:sessionId/match-report/view"
            element={user ? <AcademyMatchReportView user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/player-dashboard"
            element={user ? <AcademyPlayerDashboard user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/player/:playerId/feedback"
            element={user ? <AcademyPlayerFeedback user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/feedback/:playerId"
            element={user ? <AcademyFeedback user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/feedback"
            element={user ? <AcademyFeedback user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/attendance"
            element={user ? <AttendanceHub user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/attendance-hub"
            element={user ? <AttendanceHub user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/my-pay"
            element={user ? <CoachPay user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/coach/squads"
            element={user ? <CoachSquadBuilder user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/attendance/:sessionId"
            element={user ? <AcademyAttendance user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/attendance/player/:playerId"
            element={user ? <AcademyAttendance user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/browse-teams"
            element={user ? <AcademyBrowseTeams user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/payment-verification"
            element={user ? <AdminPaymentVerification user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/locations"
            element={user ? <AcademyLocationManagement user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/unassigned-players"
            element={user ? <AcademyUnassignedPlayers user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/player-overview"
            element={user ? <AcademyPlayerOverview user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/consent-texts"
            element={user ? <ConsentTextManagement user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/approvals"
            element={user ? <AdminApprovals user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/walk-ins"
            element={user ? <AdminWalkIns /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/trials"
            element={user ? <AdminTrials /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/trial-surveys"
            element={user ? <AdminTrialSurveys /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/holding-group"
            element={user ? <AcademyHoldingGroup /> : <Navigate to="/" />}
          />
          <Route path="/trial-survey/:token" element={<TrialSurvey />} />
          <Route
            path="/academy/pending-registrations"
            element={user ? <AdminApprovals user={effectiveUser} logout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/discount-codes"
            element={user ? <AdminDiscountCodes user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/discount-codes"
            element={user ? <AdminDiscountCodes user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/merchant-discounts"
            element={user ? <AdminMerchantDiscounts user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/merchants"
            element={user ? <AdminMerchants user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/disclaimer-settings"
            element={user ? <AdminDisclaimerSettings user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/media"
            element={user ? <AdminMediaLibrary user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/branding-settings"
            element={user ? <AdminBrandingSettings user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/branding"
            element={<Navigate to="/admin/branding-settings" replace />}
          />
          <Route
            path="/admin/pdpa-settings"
            element={user ? <AdminPDPASettings user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/force-logout"
            element={user ? <AdminForceLogout user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/whatsapp-settings"
            element={user ? <AdminWhatsAppSettings user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/fee-matrix"
            element={user ? <AdminFeeMatrix /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/handbook"
            element={user ? <AdminHandbook /> : <Navigate to="/" />}
          />
          <Route
            path="/handbook/:kind"
            element={user ? <HandbookReader /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/data-sync"
            element={user ? <AdminDataSync /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/manual-invoice"
            element={user ? <AdminManualInvoice /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/reminders-due"
            element={user ? <AdminRemindersDue /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/fee-reconciliation"
            element={user ? <AdminFeeReconciliation /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/payments"
            element={user ? <AdminPaymentsHub /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/academy-inbox"
            element={user ? <AcademyInbox /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/events"
            element={user ? <AdminEvents /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/events/master-qrs"
            element={user ? <AdminMasterQRs /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/fee-mismatches"
            element={user ? <AdminFeeMismatches /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/term3-reconciliation"
            element={user ? <AdminTerm3Reconciliation /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/duplicate-sessions"
            element={user ? <AdminDuplicateSessions /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/duplicate-registrations"
            element={user ? <AdminDuplicateRegistrations /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/migrations"
            element={user ? <AdminMigrations /> : <Navigate to="/" />}
          />
          {/* Public event registration page — no auth required */}
          <Route path="/events/:slug" element={<PublicEventPage />} />
          <Route
            path="/admin/term-payments"
            element={user ? <AdminTermPayments /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/pending-cleanup"
            element={user ? <AdminPendingCleanup /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/kit-management"
            element={user ? <AdminKitManagement /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/duplicate-players"
            element={user ? <AdminDuplicatePlayers /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/kit-orders"
            element={user ? <ParentKitOrdersPage /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/credits"
            element={user ? <AdminCredits /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/rain-out"
            element={user ? <AdminRainOut /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/registration-pipeline"
            element={user ? <RegistrationPipeline user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/duplicate-accounts"
            element={user ? <DuplicateAccounts user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/academy-roster"
            element={user ? <AcademyRoster user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/register-for-parent"
            element={user ? <AdminRegisterForParent user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/billing-adjustments"
            element={user ? <AdminBillingAdjustments user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/registration-notice-management"
            element={user ? <RegistrationNoticeManagement user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/session-upgrade"
            element={user ? <SessionUpgrade user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/parent-dashboard"
            element={user ? <ParentDashboard user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/coach-dashboard"
            element={user ? <CoachDashboard user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/coach-kit-handover"
            element={user ? <CoachKitHandover /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/coach/kit-numbers"
            element={user ? <CoachKitNumbers /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/fixtures"
            element={user ? <LeagueFixtures user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/player-feedback"
            element={user ? <PlayerFeedback user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/broadcast"
            element={user ? <CoachBroadcast user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/coach-management"
            element={user ? <CoachManagement user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/session-management"
            element={user ? <SessionManagement user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/payment/:bookingId"
            element={user ? <AdhocPaymentPage user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/data-management"
            element={user ? <DataManagement user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/academy/my-invoices"
            element={user ? <MyInvoices user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/committee"
            element={user ? <CommitteePortal user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/member-management"
            element={user && ["convenor", "captain"].includes(effectiveUser?.role) ? <MemberManagement user={effectiveUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/apply-academy"
            element={<AcademyPlaceholder />}
          />
          <Route
            path="/outstanding-payment/:token"
            element={<OutstandingPaymentPage />}
          />
          <Route path="*" element={<NotFound user={effectiveUser} />} />
        </Routes>
      </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;